import React, { useState } from 'react';
import { CampaignStageStats } from '../types';
import { STAGE_LABEL, STAGE_ORDER, pct } from '../lib';

/** The campaign funnel sliced by the lead's presales stage: one stacked bar per
 *  stage, each lead in exactly one segment by its best outcome. Colours are the
 *  validated outcome palette (read → failed), not the stage pill colours: here the
 *  colour says what happened, the row says who. Plain HTML rows, so the text
 *  stays at UI size whatever the page width. */
const SEGMENTS: [keyof CampaignStageStats, string, string][] = [
  ['read', 'Read', '#008300'],
  ['delivered', 'Delivered, not read', '#2a78d6'],
  ['pending', 'Sent, no receipt yet', '#D9D4C7'],
  ['throttled', 'Throttled by Meta', '#eda100'],
  ['failed', 'Undeliverable or invalid', '#4a3aa7'],
];

const StageOutcomeBars: React.FC<{ rows: CampaignStageStats[]; selected?: string; onSelect?: (stage: string) => void }> = ({ rows, selected, onSelect }) => {
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  const ordered = [...rows].filter((r) => r.enrolled > 0)
    .sort((a, b) => (STAGE_ORDER.indexOf(a.stage) + 1 || 99) - (STAGE_ORDER.indexOf(b.stage) + 1 || 99));
  if (ordered.length === 0) return null;
  const max = Math.max(...ordered.map((r) => r.enrolled));

  return (
    <div className="relative px-3 pb-3 pt-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-graphite pb-3">
        {SEGMENTS.map(([k, label, color]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />{label}
          </span>
        ))}
      </div>
      <div className="grid gap-1.5 min-w-[560px]" role="list" aria-label="Outcomes by lead stage">
        {ordered.map((r) => {
          const dim = selected && selected !== r.stage;
          const reached = r.read + r.delivered;
          return (
            <div key={r.stage} role="listitem"
                 className={`grid grid-cols-[96px_minmax(0,1fr)_230px] items-center gap-3 ${onSelect ? 'cursor-pointer' : ''} ${dim ? 'opacity-40' : ''}`}
                 onClick={() => onSelect && onSelect(selected === r.stage ? '' : r.stage)}>
              <span className={`text-[12px] text-right truncate ${selected === r.stage ? 'font-bold' : 'font-semibold'}`}>{STAGE_LABEL[r.stage] ?? r.stage}</span>
              <div className="h-[18px] flex gap-px" style={{ width: `${(100 * r.enrolled) / max}%` }}>
                {SEGMENTS.map(([k, label, color]) => {
                  const n = r[k] as number;
                  if (!n) return null;
                  return (
                    <div key={k} className="h-full first:rounded-l-[3px] last:rounded-r-[3px]" style={{ flex: `${n} 0 0`, background: color }}
                         onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, text: `${STAGE_LABEL[r.stage] ?? r.stage} · ${label}: ${n} of ${r.enrolled} (${pct(n, r.enrolled)})` })}
                         onMouseLeave={() => setTip(null)} />
                  );
                })}
              </div>
              <span className="text-[11.5px] text-graphite num whitespace-nowrap">
                {r.read} read · {pct(r.read, reached)} of delivered{r.replied ? ` · ${r.replied} replied` : ''}
              </span>
            </div>
          );
        })}
      </div>
      {tip && (
        <div className="fixed z-20 pointer-events-none bg-charcoal text-cream text-[12px] px-2.5 py-1.5 rounded-md shadow-sketch-btn num"
             style={{ left: Math.min(tip.x + 14, window.innerWidth - 260), top: tip.y + 14 }}>{tip.text}</div>
      )}
    </div>
  );
};

export default StageOutcomeBars;
